import { useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance, directApiInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import {
  runEngineSync,
  type EngineSyncInfo,
  type EngineSyncOutcome,
  type EngineSyncStatusPayload,
} from "../lib/engineSyncPolling";

export interface OperatingHourDay {
  open: string;
  close: string;
  // SAM-198 — recurring class schedules: additional windows on the same
  // weekday (e.g. Yoga Monday 08:00-09:00 AND 18:00-19:00). Optional — a
  // day with none omits the key entirely.
  extra_windows?: Array<{ open: string; close: string; label?: string; capacity?: string; instructor?: string }>;
  // SAM-546 — Sessions editor label-only metadata for the FIRST window of the day.
  // See `src/utils/operatingWindows.ts`'s `TimeWindow` doc for why these have no
  // reader today (capacity stays uniform via settings.intervals.max_per_slot; the
  // booking engine has no per-window capacity field to forward one to either).
  label?: string;
  capacity?: string;
  instructor?: string;
}

export interface OperatingHours {
  [day: string]: OperatingHourDay;
}

export interface BookingOption {
  id: string;
  label_en: string;
  label_es: string;
  short_desc_en: string;
  short_desc_es: string;
  price: number;
  currency: string;
  min_amount: number;
  max_amount: number;
  // SAM-195: per-role price matrix, keyed by user_type_id (as a string —
  // round-trips through JSON). Missing/empty means "flat price for
  // everyone", unchanged from before SAM-195. Never hardcode a role id here.
  prices_by_role?: Record<string, number>;
  // SAM-588: per rate-group price, keyed by rate group id. An empty cell = not sold to that
  // group. When present it wins over `prices_by_role` for this option (see utils/rateGroups.ts).
  prices_by_group?: Record<string, number>;
  // SAM-591 — only read when `pricing.price_unit === 'unit'`: what one guest of this type pays
  // beyond the unit's `included_pax` (adult 25, child 15). `price` is ignored in that mode.
  extra_guest_price?: number;
  // SAM-592: flat event price per size band, used when pricing.price_unit === 'event'.
  prices_by_event_size?: Partial<Record<'s' | 'm' | 'l' | 'xl', number>>;
}

// SAM-195 — role-pricing config, additive/optional on Service.pricing.
// Caps and requirements belong to the role, not to any one price cell.
export interface ServicePricingRoleConfig {
  max_qty_per_booking?: number;
  max_qty_per_day?: number;
}

/**
 * SAM-195 follow-up — a cap on the SUMMED quantity across several booking options, which a
 * per-option max cannot express. `included` prices the first N units in the group at zero,
 * handed out following the order of `tiers`. See functions/booking/pricing.py.
 */
export interface ServiceTierGroup {
  id: string;
  label?: { es?: string; en?: string };
  tiers: string[];
  max_total?: number;
  included?: number;
}

import type { ServiceRateGroup } from '../utils/rateGroups';
import type { ServiceSeasonPayload } from '../components/serviceSeasons';
import { seasonSaveIssue } from '../utils/pricingErrors';
/** SAM-591 — unit-priced services (cabanas): ONE price that includes `included_pax` people
 * (adults and children mixed), plus each option's `extra_guest_price` per additional person.
 * Same shape as a resource's `metadata` override. Backend: functions/booking/pricing.py. */
export interface ServiceUnitPricing {
  price?: number;
  included_pax?: number;
  max_pax?: number;
}

/** How a service is priced (`pricing.price_unit`). See functions/booking/pricing.py. */
export type PriceUnit = 'person' | 'unit' | 'event';

export interface ServicePricing {
  // SAM-591 / SAM-592: absent = 'person' (per guest, the historical behaviour); 'unit' sells the
  // booked resource at `unit.price` (+ extra_guest_price per extra guest); 'event' charges one
  // band price (prices_by_event_size) picked from the booking's total pax.
  price_unit?: PriceUnit;
  unit?: ServiceUnitPricing;
  default_user_type_id?: number;
  roles?: Record<string, ServicePricingRoleConfig>;
  tier_groups?: ServiceTierGroup[];
  // SAM-588 — tariffs with eligibility and quota; see utils/rateGroups.ts.
  rate_groups?: ServiceRateGroup[];
  // SAM-589 — price per season (date range); see components/serviceSeasons.ts
  seasons?: ServiceSeasonPayload[];
}

export interface IntervalSettings {
  duration: string;       // ISO 8601: "PT60M", "PT1H30M"
  max_per_slot: number;
  start_padding: string;  // ISO 8601: "PT0M"
  end_padding: string;    // ISO 8601: "PT0M"
  // SAM-198 field-parity pass — 'fixed' services only. How often a new bookable start time
  // repeats within `duration` (e.g. a 60-min slot offered every 15 min). Optional: absent means
  // "once per `duration`". Read by `engine_sync.py::_build_service_payload` as
  // `bookable_interval`; has no local (pre-sync) scheduling effect.
  bookable_interval?: string; // ISO 8601, e.g. "PT15M"
}

export interface CancellationPolicy {
  time_limit: string;     // ISO 8601: "PT24H"
  // NOTE (SAM-546/SAM-540): the booking engine has no field for a penalty
  // fee — `engine_sync.py` reads `cancellation_policy.time_limit` only.
  // This flag stays SAM-side only (manual/POS enforcement); it is never
  // forwarded to the engine. Keep any UI for it labelled as such.
  penalty_fee: boolean;
  // SAM-466 — share (0-100) of the cancellation credit a late cancellation loses when penalty_fee
  // is on. Absent = no deduction (functions/booking/cancellation_policy.py::credit_percent).
  penalty_percent?: number;
}

// SAM-546 (N11) — one physical/bookable unit of a service (a single cabana,
// a single tee, a single table). Optional and additive: `settings.resources`
// absent or empty means "one implicit resource", identical to every service
// today (see `engine_sync._normalize_resource_configs`'s "default" fallback
// on the backend, SAM-540/PR #343). `key` is REQUIRED once a resource is
// created by this UI and must never change after that — it is the only
// thing the backend's sync uses to recognize "this is still the same
// physical unit" across saves (`booking_engine_resource_map` is keyed by
// it). Renaming a resource must only ever change `name`, never `key`,
// or the next sync creates a brand-new engine Resource instead of updating
// the existing one — exactly the "5 catalogos duplicados" failure mode the
// ticket calls out.
export interface ServiceResourceConfig {
  key: string;
  name: string;
  // Falls back to the service's own `max_capacity` on the backend when
  // omitted — see `_normalize_resource_configs`.
  capacity?: number;
  // Falls back to `settings.intervals.max_per_slot` (fixed) / `settings.daily_capacity`
  // (day) on the backend when omitted.
  max_simultaneous_bookings?: number;
  // SAM-591 — per-unit override of `pricing.unit` (Confluence 12.2 T-1); missing keys fall back to
  // the service default. Other keys are preserved untouched on save.
  metadata?: Record<string, unknown> & ServiceUnitPricing;
}

export interface ServiceSettings {
  intervals: IntervalSettings;
  cancellation_policy?: CancellationPolicy;
  // 'day' services only (SAM-196). Vocabulary/field names adopted verbatim
  // from sam-booking-service's own Service model so nothing needs
  // translating once SAM-194 proxies to the real booking service. A plain
  // "HH:MM" applies every day; a per-weekday object overrides individual
  // days. When absent, the backend falls back to operating_hours.
  start_time?: string | Record<string, string>;
  end_time?: string | Record<string, string>;
  min_days?: number;
  max_days?: number;
  daily_capacity?: number;
  // SAM-546/SAM-540 — N resources per service. See ServiceResourceConfig.
  resources?: ServiceResourceConfig[];
  // SAM-198 Policies & Add-Ons parity pass. Round-tripped for the admin's own editing session
  // only — nothing in sam-lambda-functions reads this block today (checked against
  // origin/dev 2026-09-25: no consumer in functions/booking/). See useServiceEditor.ts's
  // FormData comment for the full field list and PoliciesSection.tsx for the "not enforced yet"
  // UI treatment.
  booking_policy?: ServiceBookingPolicy;
  // SAM-590 — see ServiceAddOn. Absent/empty = no add-ons, unchanged pre-SAM-590 behaviour.
  add_ons?: ServiceAddOn[];
  // SAM-592 — event size band for Single-day / Multi-day events (see src/utils/eventSize.ts).
  event_size?: 's' | 'm' | 'l' | 'xl';
  // SAM-656 — 'business': availability uses the business's weekly Schedule instead of this
  // service's operating_hours (kept stored, not copied). Absent = 'custom'.
  hours_source?: 'business' | 'custom';
  // SAM-657 — fixed badge key; absent = the setup's suggestion (see src/utils/serviceBadge.ts).
  badge?: 'cabana' | 'day_pass' | 'green_fee' | 'class' | 'table' | 'admission';
}

// SAM-590 — an optional charge line offered alongside a service (cart, caddie, towel, locker,
// …), distinct from a booking option: an add-on never affects capacity/schedule/provider (if it
// did, it would be a separate service instead — Rodrigo's rule). Backend shape from
// sam-lambda-functions PR #419: `settings.add_ons[]`. `get_service_description` returns it
// verbatim; booking requests price it server-side from `id`/`qty` pairs only (never a price —
// see CLAUDE.md "Never send a price from the client").
export interface ServiceAddOn {
  id: string; // slug, unique per service
  label_en: string;
  label_es: string;
  price: number;
  currency?: string;
  max_qty: number;
  required?: boolean;
  // Keyed by user_type_id (string), same convention as BookingOption.prices_by_role.
  prices_by_role?: Record<string, number>;
}

export interface ServiceBookingPolicy {
  waitlist_enabled?: boolean;
  approval_flow?: 'instant' | 'request';
  payment_requirement?: 'free' | 'cc_hold' | 'deposit' | 'full';
  payment_amount?: number | string;
  payment_hold_method?: 'cc_auth' | 'charge_refund';
  cancellation_inside_window?: 'auto' | 'request';
  cancellation_outside_window?: 'auto' | 'request' | 'none';
  age_restriction?: number | string;
  terms_conditions?: string;
}

// SAM-546 — a single closure/holiday period, read by the backend from
// `service.restrictions.availability` (existing column/shape — see
// `functions/booking/lambda_function.py::update_service_availability`).
// `status: 'unavailable'` is the only value `engine_sync.py`'s
// `closures_from_restrictions` turns into an engine ScheduleBlock; any other
// status (e.g. a future 'available' override) is preserved on save but not
// something this editor creates or shows.
export interface ServiceAvailabilityPeriod {
  start: string;   // ISO date or date-time, e.g. "2026-12-24" or "2026-12-24T00:00:00"
  end: string;
  status: 'unavailable' | 'available' | string;
  reason?: string;
}

export interface ServiceRestrictions {
  availability?: ServiceAvailabilityPeriod[];
}

// Scheduling model — adopted from sam-booking-service's Service.type.
// 'fixed' (default, today's behaviour): a time slot is chosen.
// 'flexible': variable duration between a min/max — accepted, no
//   dedicated UI yet.
// 'day': a whole DATE is chosen, no time-of-day selector.
export type ServiceType = 'fixed' | 'flexible' | 'day';

// SAM-533/SAM-545 — narrow catalog offering shapes on top of `type`. Optional:
// most services have none and keep choosing `type` directly, exactly as
// before. When set, the backend (functions/booking/catalog_taxonomy.py)
// requires `type` to match a fixed mapping, so the wizard always derives/
// locks `type` from this value instead of letting them drift apart (crud
// only cross-checks the two when BOTH are present on the same request, so
// we always send both).
//
// SAM-198 follow-up (sam-lambda-functions PR #420, `feat/SAM-533-subtype-
// vocabulary-proposal`) widened the backend's closed vocabulary from the
// original three (cabana/minconsumo/teetime) to all eleven of Rodrigo's
// "12.4 Services admin UI" prototype subtypes — this widens the client-side
// union/mapping to match, so `src/pages/ServiceEditor/create/
// serviceCreateTaxonomy.ts`'s create flow can send every one of them
// verbatim instead of falling back to a plain/custom service.
export type ServiceSubtype =
  | 'cabana' | 'minconsumo' | 'teetime'
  | 'private' | 'class' | 'stay' | 'equipment' | 'tables'
  | 'daypass' | 'single' | 'multiday';

// `equipment` is the one subtype with two valid backend scheduling types
// (hourly/session -> 'flexible', full-day -> 'day' — see catalog_taxonomy.py's
// `SERVICE_SUBTYPES['equipment']['scheduling_type']`, a 2-tuple there). This
// map carries only the default `resolve_scheduling_type` fills in when just
// the subtype is sent — 'flexible', matching the prototype's own default
// (`applySubtypeDefaults`: equipment defaults to hourly) — same as every
// other single-valued entry here.
export const SERVICE_SUBTYPE_SCHEDULING_TYPE: Record<ServiceSubtype, ServiceType> = {
  cabana: 'day',
  minconsumo: 'day',
  teetime: 'fixed',
  private: 'fixed',
  class: 'fixed',
  stay: 'day',
  equipment: 'flexible',
  tables: 'fixed',
  daypass: 'day',
  single: 'fixed',
  multiday: 'day',
};

// SAM-533 `GET /service` additive fields — bilingual catalog classification,
// separate from the internal scheduling `type`. Backend degrades: a service
// fetched before this rolls out to the dev/prod API simply omits these, so
// every field here is optional and the drawer must not assume it exists.
export interface ServiceCatalogLabel {
  id: number;
  name_en?: string | null;
  name_es?: string | null;
}

export interface ServiceVisibility {
  // null = no extra restriction beyond the business' own community
  // authorization (NOT "visible to nobody") — see add_service_catalog_taxonomy.sql.
  authorized_estate_group_ids?: number[] | null;
}

export interface Service {
  id: string;
  name: string;
  name_en?: string;
  name_es?: string;
  description?: string;
  description_en?: Array<{ order: number; title: string; content: string }>;
  description_es?: Array<{ order: number; title: string; content: string }>;
  price?: number;
  duration?: string;
  category?: string;
  status?: 'active' | 'inactive';
  // sam_core.service.is_active — the authoritative DB flag a business toggles to disable a
  // service (SAM-569). Distinct from `status`/`status_id` above, which key off the legacy
  // service_status table and do not track this. Optional because older/partial responses may
  // omit it; see isServiceActive() below for the fallback.
  is_active?: boolean;
  image_url?: string;
  // null on a PATCH clears the stored image (SAM-1009).
  logo_url?: string | null;
  background_img_url?: string | null;
  background_color?: string;
  business_id?: string;
  type?: ServiceType;
  operating_hours?: OperatingHours;
  settings?: ServiceSettings;
  // SAM-546 — closures/holidays; see ServiceRestrictions.
  restrictions?: ServiceRestrictions;
  booking_options?: BookingOption[];
  pricing?: ServicePricing | null;
  // ISO-8601 durations ("PT2H", "P30D") — the exact format `engine_sync.py`
  // forwards verbatim to the booking engine, and what `lambda_function.py`'s
  // own local-availability read path parses with `iso8601_to_timedelta` /
  // `iso8601_to_days`. Never a bare number.
  booking_window_start?: string;
  booking_window_end?: string;
  max_capacity?: number;
  availability?: number;
  service_type_id?: number;
  service_group_id?: number;
  // SAM-533 (N09/PR #339) — optional catalog subtype; see ServiceSubtype above.
  subtype?: ServiceSubtype | null;
  // Additive, read-only fields the SAM-533 catalog GET may include. Absent
  // entirely when the deployed backend predates that PR — never assumed.
  subtype_label?: { en: string; es: string } | null;
  // SAM-533 (PR #416) — nested read objects for the service_group_id /
  // service_type_id FKs above, added to GET /services and the service
  // description endpoint via LEFT JOIN. Named catalog_group/catalog_type to
  // match the existing naming from PR #339, not service_group/service_type.
  // Reuses ServiceCatalogLabel's {id, name_en, name_es} shape. Absent
  // entirely on a backend that predates that PR — never assumed.
  catalog_group?: ServiceCatalogLabel | null;
  catalog_type?: ServiceCatalogLabel | null;
  scheduling_type?: ServiceType | null;
  visibility?: ServiceVisibility | null;
  // SAM-194 step 1.5 — set once this service has been mirrored into the booking engine
  // (sam-booking-service) by POST/GET /bookings/services/{id}/engine-sync. NULL/undefined
  // means "not synced yet" (or the flag is off and this service was never synced).
  booking_engine_service_id?: string | null;
  // SAM-1184 — 'pass_fee' marks the service that prices a paid pass (a day pass / minimum spend). It is not
  // booked through the engine; New pass sells it with a payment link (SAM-1171).
  offer_type?: string | null;
  booking_engine_resource_id?: string | null;
  booking_engine_location_id?: string | null;
  // SAM-540 — per-resource engine ids (see EngineSyncResourceMapEntry); returned by /crud/service.
  booking_engine_resource_map?: Record<string, EngineSyncResourceMapEntry> | string | null;
  created_at?: string;
  updated_at?: string;
}

// SAM-194 step 1.5 — booking engine sync. Off by default; only the admins piloting the booking
// engine flip this in their env.
export const isBookingEngineSyncEnabled = (): boolean =>
  import.meta.env.VITE_BOOKING_ENGINE_SYNC === 'true';

// SAM-540 (PR #343) — per-resource engine ids, keyed the same way
// `settings.resources[].key` is (or "default" for a service with no
// explicit resources array). Only the "default"/first entry's slots
// currently come back through SAM's read path — see the resources editor's
// warning copy and the PR description's "what still doesn't reach the
// engine" note.
export interface EngineSyncResourceMapEntry {
  resource_id?: string | null;
  recurring_schedule_id?: string | null;
  weekday_block_ids?: Record<string, string>;
  closure_block_ids?: Record<string, string>;
}

export interface EngineSyncMapping {
  booking_engine_service_id: string | null;
  booking_engine_resource_id: string | null;
  booking_engine_location_id: string | null;
  booking_engine_resource_map?: Record<string, EngineSyncResourceMapEntry> | null;
}

export interface EngineSyncStatus {
  service_id: string;
  synced: boolean;
  mapping: EngineSyncMapping;
  engine_status?: { reachable: boolean; statusCode?: number; error?: string } | null;
  // SAM-568 — async engine sync. Present once the backend ships the new contract; absent on
  // today's (pre-SAM-568) deployment, which the polling logic in `src/lib/engineSyncPolling.ts`
  // treats as "no reliable signal" rather than assuming either state.
  sync?: EngineSyncInfo | null;
}

export interface ServiceResponse {
  data: Service[];
  total_pages: number;
  page: number;
  total_elements: number;
}

// SAM-569: whether a service should display/behave as "Active" in the admin UI.
// `is_active` (sam_core.service.is_active) is the authoritative source of truth — it's the flag
// the guest-facing booking endpoints now enforce. Falls back to the legacy status_id/status
// fields only when a response doesn't include is_active at all, so older/partial payloads still
// render something sensible instead of nothing.
export function isServiceActive(
  service: Pick<Service, 'is_active' | 'status'> & { status_id?: number }
): boolean {
  if (typeof service.is_active === 'boolean') return service.is_active;
  return service.status_id === 1 || service.status === 'active';
}

// Hook to fetch services for a specific business
export const useServices = (businessId: string | null) => {
  return useQuery<Service[], Error>({
    queryKey: ['services', businessId],
    queryFn: async () => {
      if (!businessId) throw new Error('Business ID is required');

      const response = await defaultInstance<ServiceResponse>({
        url: '/service',
        method: 'GET',
        params: {
          business_id: businessId
        }
      });

      const apiResponse = response.data;
      if (Array.isArray(apiResponse)) return apiResponse;
      return Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
    },
    enabled: !!businessId
  });
};

// ─── Schedule & Detail hooks (direct API — no /crud prefix) ─────────────────

export interface ServiceScheduleSlot {
  time: string;
  bookings_count: number;
  max_bookings: number;
  available?: boolean;
}

// SAM-199 follow-up — a 'day' service (settings-driven, see day_schedule.py's
// build_day_schedule_entries) returns ONE entry per date with no `slots` array at
// all: `available`/`remaining_capacity`/`opens`/`closes` describe the whole day
// directly. A 'fixed'/'flexible' service keeps returning `slots`. Both shapes are
// optional on this interface so one type covers `GET /services/schedule/{id}`'s
// response regardless of the service's `type` — check `Array.isArray(day.slots)`
// (see isDayScheduleDay in serviceBooking.ts) rather than assuming either shape.
export interface ServiceScheduleDay {
  day_of_week: string;
  open?: string;
  close?: string;
  slots?: ServiceScheduleSlot[];
  // 'day' service fields (day_schedule.py::build_day_schedule_entries) — absent
  // for 'fixed'/'flexible' services, which use `slots` instead.
  date?: string;
  available?: boolean;
  remaining_capacity?: number | null;
  opens?: string;
  closes?: string;
}

export interface ServiceScheduleData {
  id: string;
  name_en: string;
  name_es: string;
  availability: Record<string, ServiceScheduleDay>;
  // SAM-194 follow-up (incident 2026-09-20, booking_engine.resolve_schedule_source) — marks
  // whether this response came from the real booking engine or the local Postgres fallback.
  // Absent on a backend that predates the change; never assume 'engine' when it's missing.
  schedule_source?: 'engine' | 'local';
  // Only present when schedule_source === 'local' — why the engine wasn't used.
  fallback_reason?: string | null;
}

export const useServiceById = (serviceId: string | null | undefined) => {
  return useQuery<Service, Error>({
    queryKey: ['service-detail', serviceId],
    queryFn: async () => {
      const response = await directApiInstance({
        url: `/services/description/${serviceId}`,
        method: 'GET',
      });
      const r = response as any;
      return (r?.data || r) as Service;
    },
    enabled: !!serviceId,
    staleTime: 120000,
    retry: false,
  });
};

/** The owning business of a crud `service` row response (`[row]`, `{data: row}`, `{data: [row]}` or `row`). */
export function businessIdFromServiceRow(body: unknown): string | null {
  const b = body as { data?: unknown } | unknown[] | null;
  const inner = b && !Array.isArray(b) && typeof b === 'object' && 'data' in b ? (b as { data?: unknown }).data : b;
  const row = (Array.isArray(inner) ? inner[0] : inner) as { business_id?: unknown } | undefined;
  const id = row?.business_id;
  return id == null || id === '' ? null : String(id);
}

/** The crud `service` row's own columns that /services/description (the editor's main read) leaves out. */
export type ServiceRowFields = Pick<Service, 'business_id' | 'is_active' | 'background_img_url' | 'background_color'> & { status_id?: number };

function serviceRowOf(body: unknown): Record<string, unknown> | undefined {
  const b = body as { data?: unknown } | unknown[] | null;
  const inner = b && !Array.isArray(b) && typeof b === 'object' && 'data' in b ? (b as { data?: unknown }).data : b;
  const row = (Array.isArray(inner) ? inner[0] : inner) as Record<string, unknown> | undefined;
  return row && typeof row === 'object' ? row : undefined;
}

/** SAM-1009 — the cover, colour and active flag of a crud `service` row response; only the keys it carries. */
export function serviceRowFields(body: unknown): Partial<ServiceRowFields> {
  const row = serviceRowOf(body);
  if (!row) return {};
  const out: Record<string, unknown> = {};
  for (const key of ['is_active', 'status_id', 'background_img_url', 'background_color'] as const) {
    if (key in row) out[key] = row[key];
  }
  return out as Partial<ServiceRowFields>;
}

// SAM-198 — GET /services/description/{id} does not return business_id, so the editor read the
// owning business from the crud row instead of falling back to the signed-in user's business
// (which showed e.g. "Cap Cana Admin" on an Api Beach service and read the wrong business hours).
// SAM-1009 — it does not return logo_url / background_img_url / background_color / is_active either
// (Media opened empty, Active could not be read), so the same crud row feeds those fields too.
export const useServiceRow = <T = Record<string, unknown>>(
  serviceId: string | null | undefined,
  select?: (row: Record<string, unknown>) => T,
) => {
  return useQuery<Record<string, unknown>, Error, T>({
    queryKey: ['service-row', serviceId],
    queryFn: async () => {
      const response = await defaultInstance({ url: `/service/${serviceId}`, method: 'GET' });
      return serviceRowOf(response.data) ?? {};
    },
    select,
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useServiceBusinessId = (serviceId: string | null | undefined) =>
  useServiceRow<string | null>(serviceId, (row) => businessIdFromServiceRow(row));

export const useServiceSchedule = (serviceId: string | null | undefined) => {
  return useQuery<ServiceScheduleData, Error>({
    queryKey: ['service-schedule', serviceId],
    queryFn: async () => {
      try {
        const response = await directApiInstance({
          url: `/services/schedule/${serviceId}`,
          method: 'GET',
        });
        const r = response as any;
        return (r?.data || r) as ServiceScheduleData;
      } catch {
        // CORS or network error — endpoint not available from this origin
        return { id: serviceId!, name_en: '', name_es: '', availability: {} };
      }
    },
    enabled: !!serviceId,
    staleTime: 60000,
    retry: false,
  });
};

// Hook to fetch ALL services (no business filter, for admins)
export const useAllServices = () => {
  return useQuery<Service[], Error>({
    queryKey: ['services', 'all'],
    queryFn: async () => {
      const response = await defaultInstance<ServiceResponse>({
        url: '/service',
        method: 'GET',
      });

      const apiResponse = response.data;
      if (Array.isArray(apiResponse)) return apiResponse;
      return Array.isArray((apiResponse as any).data) ? (apiResponse as any).data : [];
    },
  });
};

// Create service mutation
export const useCreateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Service>) => {
      return await defaultInstance({
        url: '/service',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toaster.positive('Service created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      if (seasonSaveIssue(error)) return; // SAM-626: see useUpdateService
      toaster.negative(error?.message || 'Failed to create service', { autoHideDuration: 3000 });
    },
  });
};

// Update service mutation
export const useUpdateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Service> }) => {
      return await defaultInstance({
        url: `/service/${id}`,
        method: 'PATCH',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['service-row'] });
      toaster.positive('Service updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      // SAM-626 — a season_overlap / invalid_season 400 is reported by the editor on the rows and
      // the save bar (and by the API interceptor's toast); don't stack a generic one on top.
      if (seasonSaveIssue(error)) return;
      toaster.negative(error?.message || 'Failed to update service', { autoHideDuration: 3000 });
    },
  });
};

// Delete service mutation
export const useDeleteService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await defaultInstance({
        url: `/service/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toaster.positive('Service deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete service', { autoHideDuration: 3000 });
    },
  });
};

// --- SAM-194 step 1.5: booking engine sync ---
// Hits the booking lambda directly (not /crud) via directApiInstance, per
// `POST`/`GET /bookings/services/{service_id}/engine-sync` (sam-lambda-functions PR #330).

// SAM-546 — surfaces the backend's own EngineSyncError message (e.g. "Duplicate resource key
// 'cabana_1' in service.settings.resources...", "Engine recurring-schedule upsert returned no id
// (resource ...)") instead of a generic "Sync error"/"Booking engine sync failed" string. Shared
// by the toast (onError below) and BookingEngineSyncStatus's persistent error line, so whatever
// specific cause the engine_sync module raised is what the admin actually sees.
export function extractEngineSyncErrorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Booking engine sync failed';
}

export const useEngineSyncStatus = (serviceId: string | null | undefined) => {
  return useQuery<EngineSyncStatus, Error>({
    queryKey: ['engine-sync-status', serviceId],
    queryFn: async () => {
      const response = await directApiInstance({
        url: `/bookings/services/${serviceId}/engine-sync`,
        method: 'GET',
      });
      // httpClient's response interceptor already returns the BODY, not the axios response, so
      // `response` is {success, data} and `response.data` is the payload — `response.data.data`
      // read one level too deep and resolved to undefined. TanStack Query v5 rejects an undefined
      // result, so the widget rendered "Sync error: ... data is undefined" for every service,
      // including the ones that were correctly synced. Same unwrap the sibling hooks use.
      const r = response as any;
      return (r?.data || r) as EngineSyncStatus;
    },
    enabled: !!serviceId && isBookingEngineSyncEnabled(),
    staleTime: 30000,
    retry: false,
  });
};

// SAM-568 — GET wrapped the same way `useEngineSyncStatus` unwraps it, for the polling loop to
// read `sync` off of.
async function fetchEngineSyncStatus(serviceId: string): Promise<EngineSyncStatus> {
  const response = await directApiInstance({
    url: `/bookings/services/${serviceId}/engine-sync`,
    method: 'GET',
  });
  const r = response as any;
  return (r?.data || r) as EngineSyncStatus;
}

/**
 * SAM-568 — POST/GET-poll orchestration for `useSyncServiceToEngine`. `mutationFn` never throws:
 * every path resolves to an `EngineSyncOutcome`, and the caller decides what counts as a
 * UI-facing failure (see `onSuccess` below) — this keeps `isPending` true for the mutation's
 * entire lifetime, including any GET polling, which is exactly the "still syncing" window the
 * drawer's indicator needs.
 */
export const useSyncServiceToEngine = () => {
  const queryClient = useQueryClient();
  // One controller per in-flight sync; `cancel()` (called from the owning component's unmount
  // cleanup) flips it so the polling loop in engineSyncPolling.ts stops on its next check — no
  // further GETs, no state touched after unmount, and nothing left running.
  const cancelledRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (serviceId: string): Promise<EngineSyncOutcome> => {
      cancelledRef.current = false;

      return runEngineSync({
        post: async () => {
          try {
            const response = await directApiInstance({
              url: `/bookings/services/${serviceId}/engine-sync`,
              method: 'POST',
            });
            const r = response as any;
            return { httpStatus: r?.status ?? 200, data: (r?.data || r) as any };
          } catch (error: any) {
            // No `response` (network error/timeout) or a literal 504 both mean "the server may
            // still be working" — surface the raw error so runEngineSync can treat it as a
            // gateway timeout and fall back to polling instead of reporting a failure.
            if (!error?.response || error.response.status === 504) {
              throw error;
            }
            // A real HTTP error from the sync itself (validation, 5xx that isn't a gateway
            // timeout, etc.) — resolve with its status so runEngineSync reports it as `failed`
            // with the server's own message, same as before this change.
            return {
              httpStatus: error.response.status,
              data: error.response.data,
            };
          }
        },
        fetchStatus: async () => (await fetchEngineSyncStatus(serviceId)) as unknown as EngineSyncStatusPayload,
        isCancelled: () => cancelledRef.current,
      });
    },
    onSuccess: (outcome, serviceId) => {
      if (outcome.kind === 'cancelled') return; // drawer closed mid-sync — stay silent.

      // Ends here regardless of the specific outcome, so schedule/mapping views pick up
      // whatever the backend settled on next time they're read.
      queryClient.invalidateQueries({ queryKey: ['engine-sync-status', serviceId] });
      queryClient.invalidateQueries({ queryKey: ['services'] });

      if (outcome.kind === 'succeeded') {
        toaster.positive('Synced to the booking engine', { autoHideDuration: 3000 });
      } else if (outcome.kind === 'failed') {
        toaster.warning(outcome.message, { autoHideDuration: 4000 });
      } else if (outcome.kind === 'timeout') {
        toaster.warning(outcome.message, { autoHideDuration: 4000 });
      }
      // 'neutral' (old backend, no reliable finished-signal) is deliberately quiet here — it is
      // not an error. BookingEngineSyncStatus shows it as a non-blocking status line instead.
    },
  });

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  // Belt-and-suspenders: cancel any in-flight polling if the hook's own owner unmounts without
  // calling `cancel()` explicitly.
  useEffect(() => () => {
    cancelledRef.current = true;
  }, []);

  return { ...mutation, cancel };
};

// --- Utility functions for ISO 8601 duration ---

export function parseISODurationToMinutes(iso: string | undefined): number {
  if (!iso) return 0;
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;
  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  return hours * 60 + minutes;
}

export function minutesToISODuration(minutes: number): string {
  if (minutes <= 0) return 'PT0M';
  if (minutes >= 60 && minutes % 60 === 0) {
    return `PT${minutes / 60}H`;
  }
  if (minutes >= 60) {
    return `PT${Math.floor(minutes / 60)}H${minutes % 60}M`;
  }
  return `PT${minutes}M`;
}

export function parseISODurationToHours(iso: string | undefined): number {
  if (!iso) return 0;
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 0;
  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  return hours + minutes / 60;
}

export function hoursToISODuration(hours: number): string {
  if (hours <= 0) return 'PT0H';
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (minutes === 0) return `PT${wholeHours}H`;
  if (wholeHours === 0) return `PT${minutes}M`;
  return `PT${wholeHours}H${minutes}M`;
}

// SAM-546 — `booking_window_end` is a day-granularity ISO duration ("P30D"),
// the same format `functions/booking/lambda_function.py` defaults it to
// ("P30D") and parses with its own `iso8601_to_days`. Distinct from the
// PT-prefixed time durations above (`P30D` has no `T`).
export function parseISODurationToDays(iso: string | undefined): number {
  if (!iso) return 0;
  const match = iso.match(/^P(\d+)D$/);
  if (!match) return 0;
  return parseInt(match[1], 10);
}

export function daysToISODuration(days: number): string {
  if (!days || days <= 0) return 'P0D';
  return `P${Math.round(days)}D`;
}

// --- SAM-546 (N11) — weekly schedule helpers (client-side mirror of ---
// --- engine_sync.py's weekday_windows, for the effective-schedule preview) --

export const WEEKDAYS: Array<{ key: string; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

function normalizeClockTime(value: string): string {
  const parts = value.trim().split(':');
  if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
  return value.trim();
}

/**
 * Mirrors `functions/booking/engine_sync.py::weekday_windows` — turns
 * `operating_hours` into `{weekday: [[start,end], ...]}` window tuples,
 * splitting a midnight-crossing day (close <= open) into "tonight's tail"
 * (this weekday, up to 24:00:00) and "tomorrow's head" (next weekday, from
 * 00:00:00). Kept in sync deliberately: this is exactly what the engine
 * will compute from the same `operating_hours`, so the effective-schedule
 * preview shows what actually reaches the engine, not an approximation.
 */
export function computeWeekdayWindows(
  operatingHours: OperatingHours | undefined
): Record<string, Array<[string, string]>> {
  const windows: Record<string, Array<[string, string]>> = {};
  WEEKDAYS.forEach(({ key }) => { windows[key] = []; });

  const pushWindow = (key: string, index: number, open: string, close: string) => {
    const openTime = normalizeClockTime(open);
    const closeTime = normalizeClockTime(close);
    if (closeTime <= openTime) {
      windows[key].push([openTime, '24:00:00']);
      const nextDay = WEEKDAYS[(index + 1) % 7].key;
      windows[nextDay].push(['00:00:00', closeTime]);
    } else {
      windows[key].push([openTime, closeTime]);
    }
  };

  WEEKDAYS.forEach(({ key }, index) => {
    const dayHours = operatingHours?.[key];
    if (!dayHours?.open || !dayHours?.close) return;

    pushWindow(key, index, dayHours.open, dayHours.close);

    // SAM-198 — recurring class schedules: extra windows on the same
    // weekday, mirrored the same way as the first window.
    (dayHours.extra_windows || []).forEach((extra) => {
      if (!extra.open || !extra.close) return;
      pushWindow(key, index, extra.open, extra.close);
    });
  });

  return windows;
}
